import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { PageSection } from "@/components/site/PageSection";
import { EsLeadMagnet } from "@/components/site/EsLeadMagnet";
import { ES_PAGES, type EsPageKey } from "@/lib/es-pages";
import { EsNav } from "@/components/site/EsNav";

interface EsPageShellProps {
  page: EsPageKey;
  title?: string;
  intro?: string;
  /** Optional second intro paragraph. */
  introExtra?: string;
  banner?: ReactNode;
  /** Full-bleed hero; replaces the centered H1/intro header when provided. */
  hero?: ReactNode;
  children: ReactNode;
}

/**
 * Shared chrome for the Spanish landing pages: language switch back to the
 * English counterpart, the Spanish section nav, the H1/intro, and the closing
 * call to action. Used by every /es route so a change here updates them all.
 */
export function EsPageShell({ page, title, intro, introExtra, banner, hero, children }: EsPageShellProps) {
  const current = ES_PAGES[page];

  return (
    <main lang="es" id="contenido">
      <PageSection className="pt-4 pb-2">
        <p className="text-center text-sm text-muted-foreground">
          Esta página está en español.{" "}
          <a href={current.en} hrefLang="en" className="font-medium text-brand-logo hover:underline">
            Read this page in English
          </a>
        </p>
      </PageSection>

      <PageSection as="div" className="pb-3">
        <EsNav />
      </PageSection>

      {hero ?? (
        <PageSection className="pt-2 pb-4 text-center">
          <h1 className="text-center font-serif text-3xl md:text-4xl text-brand-deep">{title}</h1>
          {banner}
          <p className="mx-auto mt-3 max-w-3xl text-muted-foreground">{intro}</p>
          {introExtra ? (
            <p className="mx-auto mt-3 max-w-3xl text-muted-foreground">{introExtra}</p>
          ) : null}


          {/* Above-the-fold contact CTA: every /es page except the contact page
              itself gets the same prominent path to the Spanish form and phone. */}
          <div
            className={`mt-5 flex flex-wrap items-center justify-center gap-3 ${page === "contact" ? "hidden" : ""}`}
          >
            <Link
              to="/es/contacto"
              className="inline-flex items-center rounded-full bg-primary px-7 py-3 text-base font-semibold text-primary-foreground hover:opacity-90"
            >
              Contacto — Pedir una Cita
            </Link>
            <a
              href="tel:7037099771"
              aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
              className="inline-flex items-center gap-2 rounded-full border border-brand-deep px-7 py-3 text-base font-semibold text-brand-deep hover:bg-brand-soft/60"
            >
              <Phone size={18} aria-hidden="true" />
              (703) 709-9771
            </a>
          </div>
        </PageSection>
      )}

      {children}

      {/* Spanish lead magnet: shown on every /es page from this one place. */}
      <EsLeadMagnet />

      <PageSection className="py-8">
        <div className="rounded-2xl bg-brand-soft/50 p-6 text-center md:p-8">
          <h2 className="font-serif text-2xl text-brand-deep">
            ¿Listo para dar el primer paso?
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-foreground/80">
            Llame a nuestro consultorio en Herndon, Virginia, o envíenos un mensaje. Con gusto
            atendemos sus preguntas y le explicamos qué opción se ajusta mejor a su caso.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:7037099771"
              aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <Phone size={16} aria-hidden="true" />
              (703) 709-9771
            </a>
            <Link
              to="/es/contacto"
              className="inline-flex items-center rounded-full border border-brand-deep px-6 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
            >
              Escríbanos
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            El resto de nuestro sitio está disponible en inglés. Si prefiere hablar en español,
            indíquelo al llamar o en su mensaje.
          </p>
        </div>
      </PageSection>
    </main>
  );
}

/** Simple prose section with a centered brand heading, used across /es pages. */
export function EsSection({
  id,
  heading,
  children,
  maxWidth = "4xl",
}: {
  id?: string;
  heading: string;
  children: ReactNode;
  maxWidth?: "4xl" | "7xl";
}) {
  // Wide sections (data tables) trade the deep prose gutters for full width.
  const wide = maxWidth === "7xl";
  return (
    <PageSection
      className="py-4"
      maxWidth={maxWidth}
      innerClassName={wide ? "lg:px-8 xl:px-8 2xl:px-8" : undefined}
    >
      <h2 id={id} className="text-center font-serif text-2xl text-brand-deep">
        {heading}
      </h2>
      <div className="mt-3 space-y-3 text-foreground/85">{children}</div>
    </PageSection>
  );
}
